import { Inject, Injectable, InjectionToken } from '@angular/core';
import { LOCAL_STORAGE, StorageService, StorageTranscoders, isStorageAvailable} from 'ngx-webstorage-service';
import { Message } from '../components/asistencia-chat/models/message';



export const LOCAL_SERVICE_STORAGE =
    new InjectionToken<StorageService>('LOCAL_SERVICE_STORAGE');


const PARTICIPANTID_KEY = 'participantID';
const MESSAGES_KEY = 'messages';
const USER_SESSION_KEY = 'user_session';
const TIMEOUT_KEY = 'timeout';
const AGENTE_KEY = 'agentName';
const WINDOW_STATUS = 'windowStatus'; // valores: min, max
const HANDLER_TYPE = 'handler'; // chat o email o menuArbol
const HANDLER_ACCION_TYPE = 'handler_accion';
const MAXQUEUE = 'maxqueue';
const WELCOMEMSG = 'welcomemsg';
const SERVICE_ID = 'serviceID'; // Usado para mantener solo un CHAT por servicio activo - Identifica el servicio donde se instancia el asistente
const ASISTENTE_ID = 'asistenteID'; // Identifica el asistente a mostrar
const SERVICE_DESC = 'serviceDesc'; // Idem clave anterior; se usa en mensaje informativo
const URL_FROM_CHAT = 'urlFromChat'; // URL del portal donde se inicia el chat
const ID_MENU_ACCION = "idMenuAccion";

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService  {

  constructor(
    @Inject(LOCAL_SERVICE_STORAGE) private storage: StorageService
    ) {
  }

  // Verificamos si el navegador soporta localstorage
  public browserSupportStorage(): boolean {
    const localStorageAvailable = isStorageAvailable(localStorage);
    return localStorageAvailable;
  }

  public getParticipantID(): string {
    return this.storage.get(PARTICIPANTID_KEY, StorageTranscoders.STRING);
  }

  public setParticipantID(participantID: string): void {
    this.storage.set(PARTICIPANTID_KEY, participantID, StorageTranscoders.STRING);
  }

  public removeParticipantID() {
    this.storage.remove(PARTICIPANTID_KEY);
  }

  public getTimeout(): boolean {
    return this.storage.get(TIMEOUT_KEY, StorageTranscoders.BOOLEAN);
  }

  public setTimeout(timeout: boolean): void {
    this.storage.set(TIMEOUT_KEY, timeout, StorageTranscoders.BOOLEAN);
  }

  public isTimeout(): boolean {
    return this.storage.get(TIMEOUT_KEY) === true;
  }

  public existsPartcipantID(): boolean {
    return this.storage.has(PARTICIPANTID_KEY);
  }

  public getMessages(): Message[] {
    return this.storage.get(MESSAGES_KEY, StorageTranscoders.JSON);
  }

  public setMessages(messages: Message[]): void {
    this.storage.set(MESSAGES_KEY, messages, StorageTranscoders.JSON);
  }

  public existsMessages(): boolean {
    return this.storage.has(MESSAGES_KEY);
  }

  public getUserSession(): string {
    return this.storage.get(USER_SESSION_KEY, StorageTranscoders.STRING);
  }

  public setUserSession(userSession: string): void {
    this.storage.set(USER_SESSION_KEY, userSession, StorageTranscoders.STRING);
  }

  public getAgenteAssigned(): string {
    return this.storage.get(AGENTE_KEY, StorageTranscoders.STRING);
  }

  public setAgenteAssigned(agenteAssigned: string): void {
    this.storage.set(AGENTE_KEY, agenteAssigned, StorageTranscoders.STRING);
  }

  public getWindowStatus(): string {
    return this.storage.get(WINDOW_STATUS, StorageTranscoders.STRING);
  }

  public setWindowStatus(windowStatus: string): void {
    this.storage.set(WINDOW_STATUS, windowStatus, StorageTranscoders.STRING);
  }

  public removeWindowsStatus(): void{
    this.storage.remove(HANDLER_TYPE);
  }

  public getHandlerAccionType(): string {
    return this.storage.get(HANDLER_ACCION_TYPE, StorageTranscoders.STRING);
  }

  public setHandlerAccionType(handlerType: string): void {
    this.storage.set(HANDLER_ACCION_TYPE, handlerType, StorageTranscoders.STRING);
  }

  public removeHandlerAccionType(): void {
    this.storage.remove(HANDLER_ACCION_TYPE);
  }

  public getHandlerType(): string {
    return this.storage.get(HANDLER_TYPE, StorageTranscoders.STRING);
  }

  public setHandlerType(handlerType: string): void {
    this.storage.set(HANDLER_TYPE, handlerType, StorageTranscoders.STRING);
  }

  public removeHandlerType(): void {
    this.storage.remove(HANDLER_TYPE);
  }

  public getMaxQueue(): number {
    return this.storage.get(MAXQUEUE, StorageTranscoders.NUMBER);
  }

  public setMaxQueue(maxqueue: number): void {
    this.storage.set(MAXQUEUE, maxqueue, StorageTranscoders.NUMBER);
  }

  public getWelcomeMsg(): string {
    return this.storage.get(WELCOMEMSG, StorageTranscoders.STRING);
  }

  public setWelcomeMsg(welcomemsg: string): void {
    this.storage.set(WELCOMEMSG, welcomemsg, StorageTranscoders.STRING);
  }

  public getServiceID(): string {
    return this.storage.get(SERVICE_ID, StorageTranscoders.STRING);
  }

  public setServiceID(serviceID: string): void {
    this.storage.set(SERVICE_ID, serviceID, StorageTranscoders.STRING);
  }

  public getAsistenteID(): string {
    return this.storage.get(ASISTENTE_ID, StorageTranscoders.STRING);
  }

  public setAsistenteID(asistenteID: string): void {
    this.storage.set(ASISTENTE_ID, asistenteID, StorageTranscoders.STRING);
  }

  public getServiceDesc(): string {
    return this.storage.get(SERVICE_DESC, StorageTranscoders.STRING);
  }

  public setServiceDesc(serviceDesc: string): void {
    this.storage.set(SERVICE_DESC, serviceDesc, StorageTranscoders.STRING);
  }

  public getUrlFromChat(): string {
    return this.storage.get(URL_FROM_CHAT, StorageTranscoders.STRING);
  }

  public setUrlFromChat(urlFromChat: string): void {
    this.storage.set(URL_FROM_CHAT, urlFromChat, StorageTranscoders.STRING);
  }

  public setIdMenuAccion(idMenuAccion: string): void {
    this.storage.set(ID_MENU_ACCION, idMenuAccion, StorageTranscoders.STRING);
  }

  public getIdMenuAccion(): string {
    return this.storage.get(ID_MENU_ACCION, StorageTranscoders.STRING);
  }
  
  public removeIdMenuAccion(): void {
    this.storage.remove(ID_MENU_ACCION);
  }

  public cleanStorage(): void {
    this.storage.remove(PARTICIPANTID_KEY);
    this.storage.remove(MESSAGES_KEY);
    this.storage.remove(USER_SESSION_KEY);
    this.storage.remove(TIMEOUT_KEY);
    this.storage.remove(AGENTE_KEY);
    this.storage.remove(WINDOW_STATUS);
    this.storage.remove(HANDLER_TYPE);
    this.storage.remove(MAXQUEUE);
    this.storage.remove(SERVICE_ID);
    this.storage.remove(ASISTENTE_ID);
    this.storage.remove(SERVICE_DESC);
    this.storage.remove(URL_FROM_CHAT);
    this.storage.remove(ID_MENU_ACCION);
    // NO se debe hacer el clean del Welcome message para no tener efecto secuendario en refresh
    //this.storage.remove(WELCOMEMSG);
  }


}
